import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export interface WarmupScheduleRow {
  id: string;
  stage: number;
  action_type: string;
  action_params: Record<string, unknown> | null;
  scheduled_at: string;
  executed_at: string | null;
  status: "pending" | "done" | "failed" | "skipped";
  error: string | null;
  attempts: number;
}

export interface WarmupSummary {
  account_id: string;
  started_at: string | null;
  completed: boolean;
  total: number;
  pending: number;
  done: number;
  failed: number;
}

const SCHEDULE_KEY = (id: string) => ["warmup", "schedule", id] as const;

export function useWarmupSchedule(accountId: string | null) {
  return useQuery<WarmupScheduleRow[]>({
    queryKey: SCHEDULE_KEY(accountId ?? ""),
    enabled: accountId !== null,
    queryFn: () =>
      api.get<WarmupScheduleRow[]>(`/api/warmup/schedule/${accountId}`),
    // Refetch every 30s when the row is expanded — quickly shows progress
    // as the cron worker ticks rows from PENDING to DONE.
    refetchInterval: 30_000,
  });
}

export function useStartWarmup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (accountId: string) =>
      api.post<WarmupSummary>(`/api/warmup/plan/${accountId}`),
    onSuccess: (_d, accountId) => {
      qc.invalidateQueries({ queryKey: SCHEDULE_KEY(accountId) });
      qc.invalidateQueries({ queryKey: ["accounts"] });
    },
  });
}

export function useRestartWarmup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (accountId: string) =>
      api.post<WarmupSummary>(`/api/warmup/restart/${accountId}`),
    onSuccess: (_d, accountId) => {
      qc.invalidateQueries({ queryKey: SCHEDULE_KEY(accountId) });
      qc.invalidateQueries({ queryKey: ["accounts"] });
    },
  });
}

export interface MaintenanceStatus {
  account_id: string;
  enabled: boolean;
  pending: number;
  done_last_7d: number;
  failed_last_7d: number;
  horizon_at: string | null;
}

const MAINTENANCE_KEY = (id: string) => ["warmup", "maintenance", id] as const;

export function useMaintenanceStatus(accountId: string | null) {
  return useQuery<MaintenanceStatus>({
    queryKey: MAINTENANCE_KEY(accountId ?? ""),
    enabled: accountId !== null,
    queryFn: () =>
      api.get<MaintenanceStatus>(`/api/warmup/maintenance/${accountId}`),
    refetchInterval: 60_000,
  });
}

export function useSetMaintenanceEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, enabled }: { accountId: string; enabled: boolean }) =>
      api.post<MaintenanceStatus>(`/api/warmup/maintenance/${accountId}`, {
        enabled,
      }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: MAINTENANCE_KEY(vars.accountId) });
      qc.invalidateQueries({ queryKey: ["accounts"] });
    },
  });
}

export function useSkipWarmup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (accountId: string) =>
      api.post<void>(`/api/warmup/skip/${accountId}`),
    onSuccess: (_d, accountId) => {
      qc.invalidateQueries({ queryKey: SCHEDULE_KEY(accountId) });
      qc.invalidateQueries({ queryKey: ["accounts"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Per-company warmup channel pool
// ---------------------------------------------------------------------------

export interface WarmupChannelRow {
  id: string;
  value: string;
  language: string | null;
  is_global: boolean;
  metadata_json: Record<string, unknown> | null;
}

const CHANNELS_KEY = (includeGlobal: boolean) =>
  ["warmup", "channels", { includeGlobal }] as const;

export function useWarmupChannels(includeGlobal = true) {
  return useQuery<WarmupChannelRow[]>({
    queryKey: CHANNELS_KEY(includeGlobal),
    queryFn: () =>
      api.get<WarmupChannelRow[]>(
        `/api/warmup/channels?include_global=${includeGlobal}`,
      ),
  });
}

export function useAddWarmupChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      value: string;
      language?: string | null;
      metadata_json?: Record<string, unknown> | null;
    }) => api.post<WarmupChannelRow>("/api/warmup/channels", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["warmup", "channels"] });
    },
  });
}

export function useAddWarmupChannelsBulk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      channels: Array<{
        value: string;
        language?: string | null;
        metadata_json?: Record<string, unknown> | null;
      }>;
    }) => api.post<WarmupChannelRow[]>("/api/warmup/channels/bulk", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["warmup", "channels"] });
    },
  });
}

export function useDeleteWarmupChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<void>(`/api/warmup/channels/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["warmup", "channels"] });
    },
  });
}
